import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PaywallDialog } from './PaywallDialog';

interface PaywallWrapperProps {
  children: React.ReactNode;
  isEssentialEightRoute: boolean;
  isPrivacyActRoute?: boolean;
}

/**
 * PaywallWrapper - A higher-level component that intercepts navigation attempts
 * when on the Essential Eight route and shows a paywall dialog instead
 */
export function PaywallWrapper({ children, isEssentialEightRoute, isPrivacyActRoute }: PaywallWrapperProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Intercept all link clicks if we're on the essential-eight or privacy-act route
  useEffect(() => {
    if (!isEssentialEightRoute && !isPrivacyActRoute) return;

    // Function to handle click events
    const handleClick = (e: MouseEvent) => {
      // Check if the click is on an anchor tag
      const target = e.target as HTMLElement;
      const anchor = target.tagName === 'A' 
        ? target 
        : target.closest('a');
      
      if (anchor) {
        const href = (anchor as HTMLAnchorElement).getAttribute('href');
        
        // Skip if it's a dashboard link, hash link, or external link
        if (href === '/dashboard' || href === '#' || href?.startsWith('http')) {
          return;
        }
        
        // Prevent default navigation and show dialog
        e.preventDefault();
        e.stopPropagation();
        setDialogOpen(true);
        return false;
      }
    };

    // Add event listener for click capture phase
    document.addEventListener('click', handleClick, true);
    
    // Clean up event listener
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [isEssentialEightRoute]);

  return (
    <>
      {children}
      <PaywallDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
